import React, { useRef } from 'react';
import { 
  CloudLightning, 
  Wind, 
  Droplets, 
  Mountain, 
  ThermometerSun,
  Lock,
  Flame
} from 'lucide-react';
import { useWeather } from '../../context';

interface CatastropheType {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  externalUrl?: string;
  modelConfig?: {
    model: string;
    parameter?: string;
  };
}

const catastrophes: CatastropheType[] = [
  {
    id: 'extreme-weather',
    name: 'Extreme Weather',
    icon: <ThermometerSun className="w-5 h-5" />,
    description: 'Monitor extreme temperatures and weather',
    modelConfig: {
      model: 'openmeteo',
      parameter: 'temperature_2m_max'
    }
  },
  {
    id: 'severe-storms',
    name: 'Severe Convective Storms',
    icon: <CloudLightning className="w-5 h-5" />,
    description: 'Thunderstorms, tornadoes, and hail events',
    modelConfig: {
      model: 'xweather',
      parameter: 'storm_reports'
    }
  },
  {
    id: 'hurricanes',
    name: 'Hurricanes & Windstorms',
    icon: <Wind className="w-5 h-5" />,
    description: 'Tropical cyclones and coastal storms',
    externalUrl: 'https://demo.catxre.com/dashboard/explorer/windstorm/'
  },
  {
    id: 'drought',
    name: 'Drought',
    icon: <Droplets className="w-5 h-5" />,
    description: 'Monitor extended periods of water deficit',
    modelConfig: {
      model: 'drought'
    }
  },
  {
    id: 'earthquakes',
    name: 'Earthquakes',
    icon: <Mountain className="w-5 h-5" />,
    description: 'Track seismic activity and ground movement',
    externalUrl: 'https://demo.catxre.com/dashboard/explorer/earthquake/'
  },
  {
    id: 'wildfires',
    name: 'Wildfires (Realtime)',
    icon: <Flame className="w-5 h-5" />,
    description: 'Monitor active wildfires and fire risk',
    externalUrl: 'https://wildfire-app.trycatamaran.com/'
  }
  
];

export function CatastrophesPanel() {
  const { setModelSelect, setParameterSelect } = useWeather();
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  const handleSelect = (catastrophe: CatastropheType) => {
    if (catastrophe.externalUrl) {
      window.location.href = catastrophe.externalUrl;
      return;
    }

    if (catastrophe.modelConfig) {
      setModelSelect(catastrophe.modelConfig.model);
      // Only set parameter if it exists
      if (catastrophe.modelConfig.parameter) {
        setParameterSelect(catastrophe.modelConfig.parameter);
      }
      
      // Find the closest scrollable parent (sidebar)
      const sidebar = sidebarRef.current?.closest('.glass-panel');
      if (sidebar) {
        // Smooth scroll to top
        sidebar.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  };

  return (
    <div ref={sidebarRef}>
      <h3 className="text-lg font-semibold mb-4">
        Other Catastrophes
      </h3>
      <div className="space-y-3">
        {catastrophes.map((cat) => (
          <button
            key={cat.id}
            onClick={() => handleSelect(cat)}
            className="w-full glass-panel p-3 flex items-center gap-3 hover:bg-[var(--accent-bg)] transition-colors duration-200 group"
          >
            <div className="text-[var(--accent-color)] group-hover:scale-110 transition-transform">
              {cat.icon}
            </div>
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-medium">{cat.name}</h4>
                {cat.externalUrl && (
                  <Lock className="w-4 h-4 text-[var(--secondary-text)]" />
                )}
              </div>
              <p className="text-xs text-[var(--secondary-text)]">
                {cat.description}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}